import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  BrandingProvider,
  LeaderboardList,
  LeaderboardRow,
  LeaderboardShell,
  formatLapTime,
  useOrientation,
  leaderboardCarLabels,
} from "@vms/leaderboard-ui";
import type { VenueLeaderboardQuery } from "@vms/types";
import {
  useBrandingTemplate,
  useLeaderboardRecord,
  useMultiVenueLeaderboard,
  useVenueLeaderboard,
} from "../../api/leaderboards";

/**
 * Chrome-less leaderboard display for the cloud-ui — mirror of hub-ui's
 * `apps/hub-ui/src/pages/display/LeaderboardDisplay.tsx`. Mounted outside the
 * cloud-ui shell layout so it renders bare (no sidebar / topbar) and is
 * suitable for projecting on a venue TV.
 *
 * Real-time refresh: `useVenueLeaderboard` subscribes to the venue's WS room
 * via `useVenueSubscription`, then `useInvalidateOn("leaderboard", refetch)`
 * triggers a refetch every time the cloud emits an entity-changed envelope
 * after a lap-time delta lands. Same UX as hub-ui inside a venue.
 */
export function LeaderboardDisplay() {
  const { leaderboardId } = useParams<{ leaderboardId: string }>();
  const { isLandscape } = useOrientation();

  const { data: record, loading: recordLoading, error: recordError } =
    useLeaderboardRecord(leaderboardId);
  const { data: branding } = useBrandingTemplate(record?.brandingTemplateId ?? null);

  const filters: VenueLeaderboardQuery | null = useMemo(() => {
    if (!record) return null;
    return {
      tracks: record.tracks.length ? record.tracks : undefined,
      cars: record.cars.length ? record.cars : undefined,
      eventIds: record.eventIds.length ? record.eventIds : undefined,
      game: record.game ?? undefined,
      dateFrom: record.dateFrom ?? undefined,
      dateTo: record.dateTo ?? undefined,
      limit: record.entryLimit ?? undefined,
    };
  }, [record]);

  // Discriminate by which scope field is populated:
  //   - record.venueId set → hub-authored, single-venue (existing path)
  //   - record.venueId null → cloud-authored cross-venue:
  //       record.venueIds is null  → all org venues (server resolves)
  //       record.venueIds is array → explicit subset
  // Both hooks always called (rules of hooks); only one is enabled at a time.
  const isMultiVenue = record != null && record.venueId == null;
  const singleScope = isMultiVenue ? undefined : record?.venueId ?? undefined;
  const multiScope = isMultiVenue
    ? record?.venueIds && record.venueIds.length > 0
      ? { venueIds: record.venueIds }
      : { organisationId: record!.organisationId }
    : null;

  const single = useVenueLeaderboard(singleScope, isMultiVenue ? null : filters);
  const multi = useMultiVenueLeaderboard(multiScope, isMultiVenue ? filters : null);
  const entries = isMultiVenue ? multi.data : single.data;
  const entriesLoading = isMultiVenue ? multi.loading : single.loading;
  const entriesError = isMultiVenue ? multi.error : single.error;

  const leaderboard = entries ?? [];
  const carLabels = leaderboardCarLabels(leaderboard);
  const title = record?.name ?? "Leaderboard";
  const subtitle = record?.subheading ?? undefined;

  const fastestSectors = useMemo(() => {
    let s1: number | null = null;
    let s2: number | null = null;
    let s3: number | null = null;
    for (const e of leaderboard) {
      if (e.bestLapSector1Ms != null && (s1 == null || e.bestLapSector1Ms < s1)) {
        s1 = e.bestLapSector1Ms;
      }
      if (e.bestLapSector2Ms != null && (s2 == null || e.bestLapSector2Ms < s2)) {
        s2 = e.bestLapSector2Ms;
      }
      if (e.bestLapSector3Ms != null && (s3 == null || e.bestLapSector3Ms < s3)) {
        s3 = e.bestLapSector3Ms;
      }
    }
    return { sector1Ms: s1, sector2Ms: s2, sector3Ms: s3 };
  }, [leaderboard]);

  const errorMessage = recordError ?? entriesError;
  const isInitialLoad = (recordLoading && !record) || (entriesLoading && entries === null);

  return (
    <BrandingProvider branding={branding}>
      <LeaderboardShell title={title} subtitle={subtitle} mode="venue">
        {errorMessage ? (
          <div className="flex items-center justify-center h-full text-red-300 text-lg">
            {errorMessage}
          </div>
        ) : isInitialLoad ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-lg">
            Loading…
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-lg">
            No lap times recorded yet.
          </div>
        ) : (
          <LeaderboardList
            totalCount={leaderboard.length}
            renderEntry={(index) => {
              const entry = leaderboard[index];
              return (
                <LeaderboardRow
                  position={entry.position}
                  driverName={`${entry.driver.firstName} ${entry.driver.lastName}`}
                  companyName={entry.driver.companyName || undefined}
                  car={carLabels[index]}
                  lapTime={formatLapTime(entry.bestLapTimeMs)}
                  sectors={{
                    s1: entry.bestLapSector1Ms,
                    s2: entry.bestLapSector2Ms,
                    s3: entry.bestLapSector3Ms,
                  }}
                  fastestSectors={fastestSectors}
                  showSectors={isLandscape}
                  extraColumns={[{ label: "Track", value: entry.trackName ?? entry.track }]}
                />
              );
            }}
          />
        )}
      </LeaderboardShell>
    </BrandingProvider>
  );
}
